import type { IntrinsicSystemProps } from '@hover/blueprint';
import { Icon, forwardRef, system, transition } from '@hover/blueprint';
import { resetFocus } from '@hover/blueprint/foundation';
import { iArrowLeft, iArrowRight } from '@hover/icons';
import React from 'react';

import { useHomeStyles } from '../HomeStyles';

export interface StyleArrowProps extends IntrinsicSystemProps<'button'> {
  direction: 'next' | 'previous';
}

const StyleArrow = forwardRef<StyleArrowProps, 'button'>(
  ({ direction, ...props }, ref) => {
    const { background } = useHomeStyles();
    const isPrevious = direction === 'previous';

    const outside = `translate(${isPrevious ? '-50%' : '50%'}, -50%)`;
    const inside = 'translate(0, -50%)';

    return (
      <system.button
        _focusVisible={{
          opacity: 1,
          transform: inside,
          '& .focus-indicator': { opacity: 0.75 },
          '& .icon': { color: background, filter: 'brightness(200%)' },
        }}
        _groupHover={{ opacity: 1, transform: inside }}
        alignItems="center"
        bg="transparent"
        border="none"
        cursor="pointer"
        display="flex"
        justifyContent="center"
        opacity={0}
        p="0"
        sx={resetFocus()}
        transform={outside}
        {...transition.easing250('transform', 'opacity')}
        {...props}
        ref={ref}
      >
        <Icon
          className="focus-indicator"
          color={background}
          filter="brightness(0.5)"
          icon={isPrevious ? iArrowLeft : iArrowRight}
          opacity={0}
          position="absolute"
          size="medium"
          strokeWidth="6px"
          zIndex={-1}
          {...transition.easing250('opacity', 'color')}
        />
        <Icon
          className="icon"
          color="neutral.0"
          icon={isPrevious ? iArrowLeft : iArrowRight}
          size="medium"
          {...transition.easing250('color', 'filter')}
        />
      </system.button>
    );
  },
);

export { StyleArrow };
